/**
 * Forgot Password Page
 *
 * Request password reset email.
 */

import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { FiMail, FiArrowLeft, FiAlertCircle } from 'react-icons/fi'
import { authApi, isValidEmail } from '../../api'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Alert, AlertTitle } from '../../components/ui/alert'

export function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)

  // Validation
  let emailError = ''
  if (touched) {
    if (!email) {
      emailError = 'Email is required'
    } else if (!isValidEmail(email)) {
      emailError = 'Invalid email format'
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched(true)
    setError(null)

    if (!email || !isValidEmail(email)) return

    setIsLoading(true)

    try {
      const response = await authApi.forgotPassword({ email })

      if (response.success) {
        setIsSubmitted(true)
      } else {
        setError(response.error ?? 'Failed to send reset email')
      }
    } catch {
      setError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4">
        <div className="w-full max-w-md p-8 bg-background rounded-[var(--radius-card)] border border-border shadow-sm text-center">
          <div className="flex flex-col gap-6 items-center">
            <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center">
              <FiMail className="w-8 h-8 text-white" />
            </div>

            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold text-foreground">
                Check your email
              </h1>
              <p className="text-foreground-muted">
                We've sent password reset instructions to{' '}
                <span className="font-semibold text-foreground">{email}</span>
              </p>
            </div>

            <p className="text-sm text-foreground-muted">
              Didn't receive the email? Check your spam folder or{' '}
              <button
                className="text-primary hover:underline cursor-pointer"
                onClick={() => {
                  setIsSubmitted(false)
                  setEmail('')
                  setTouched(false)
                }}
              >
                try again
              </button>
            </p>

            <RouterLink to="/login">
              <Button variant="outline">
                <FiArrowLeft className="mr-2" />
                Back to login
              </Button>
            </RouterLink>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4">
      <div className="w-full max-w-md p-8 bg-background rounded-[var(--radius-card)] border border-border shadow-sm">
        <div className="flex flex-col gap-6">
          {/* Back Link */}
          <RouterLink
            to="/login"
            className="text-foreground-muted text-sm flex items-center gap-1 hover:text-foreground"
          >
            <FiArrowLeft />
            Back to login
          </RouterLink>

          {/* Header */}
          <div className="flex flex-col gap-2 text-center">
            <h1 className="text-2xl font-bold text-foreground">
              Forgot your password?
            </h1>
            <p className="text-foreground-muted">
              Enter your email and we'll send you a link to reset it.
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <FiAlertCircle className="h-4 w-4" />
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4">
              {/* Email Field */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder="you@example.com"
                />
                {emailError && <p className="text-sm text-error">{emailError}</p>}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send reset link'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
